import { useCallback, useEffect, useMemo } from 'react';
import useVerifyGoogleIntegration from 'hooks/services/google/useVerifyGoogleIntegration';
import { useAuth } from 'context/auth';
import useVerifyOutlookIntegration from 'hooks/services/outlook/useVerifyOutlookIntegration';
import { EmailIntegrationType } from 'crono-fe-common/types/emailSettings';
import useVerifyCustomEmailProviderIntegration from 'hooks/services/customEmailIntegration/useVerifyCustomEmailProviderIntegration';
import useGetEmailMailbox from 'hooks/services/email/useGetEmailMailbox';

type IProps =
  | {
      integrationTypeToTest: EmailIntegrationType | null;
      email: string | null;
      verifyDefault?: undefined;
    }
  | {
      integrationTypeToTest?: undefined;
      email?: undefined;
      verifyDefault: true;
    };

const useEmailIntegrationGuard = ({
  email,
  integrationTypeToTest,
  verifyDefault,
}: IProps) => {
  const { user } = useAuth();

  const { data: emailMailbox } = useGetEmailMailbox(false);

  const defaultMailbox = useMemo(() => {
    return emailMailbox?.data.data.find((mailbox) => mailbox.default);
  }, [emailMailbox]);

  const {
    isLoading: isLoadingGoogle,
    isError: isErrorGoogle,
    isSuccess: isSuccessGoogle,
    verifyGoogleIntegration,
    error: errorGoogle,
    data: dataGoogle,
  } = useVerifyGoogleIntegration();

  const {
    isLoading: isLoadingOutlook,
    isError: isErrorOutlook,
    isSuccess: isSuccessOutlook,
    verifyOutlookIntegration,
    error: errorOutlook,
    data: dataOutlook,
  } = useVerifyOutlookIntegration();

  const {
    isLoading: isLoadingCustomEmailProvider,
    isError: isErrorCustomEmailProvider,
    isSuccess: isSuccessCustomEmailProvider,
    verifyCustomEmailProviderIntegration,
    error: errorCustomEmailProvider,
    data: dataCustomEmailProvider,
  } = useVerifyCustomEmailProviderIntegration();
  const checkHasEmail = user?.otherSettings?.hasEmail;

  const handleVerify = useCallback(() => {
    if (!checkHasEmail) return;
    if (!verifyDefault) {
      if (!email || !integrationTypeToTest) return;
      verifyIntegration(integrationTypeToTest, email);
    } else {
      if (defaultMailbox) {
        verifyIntegration(defaultMailbox.type, defaultMailbox.email);
      }
    }
  }, [
    checkHasEmail,
    verifyDefault,
    email,
    integrationTypeToTest,
    defaultMailbox,
  ]);

  useEffect(() => {
    handleVerify();
  }, [checkHasEmail, integrationTypeToTest, email, emailMailbox]);

  const verifyIntegration = (
    emailType: EmailIntegrationType,
    email: string,
  ) => {
    if (emailType === EmailIntegrationType.GMAIL) {
      verifyGoogleIntegration(email);
    } else if (emailType === EmailIntegrationType.OUTLOOK) {
      verifyOutlookIntegration(email);
    } else if (emailType === EmailIntegrationType.CUSTOM) {
      verifyCustomEmailProviderIntegration(email);
    }
  };

  return {
    isLoading:
      integrationTypeToTest === EmailIntegrationType.GMAIL
        ? isLoadingGoogle
        : integrationTypeToTest === EmailIntegrationType.OUTLOOK
          ? isLoadingOutlook
          : integrationTypeToTest === EmailIntegrationType.CUSTOM
            ? isLoadingCustomEmailProvider
            : isLoadingGoogle ||
              isLoadingOutlook ||
              isLoadingCustomEmailProvider,
    isError:
      integrationTypeToTest === EmailIntegrationType.GMAIL
        ? isErrorGoogle
        : integrationTypeToTest === EmailIntegrationType.OUTLOOK
          ? isErrorOutlook
          : integrationTypeToTest === EmailIntegrationType.CUSTOM
            ? isErrorCustomEmailProvider
            : isErrorGoogle || isErrorOutlook || isErrorCustomEmailProvider,
    isSuccess:
      integrationTypeToTest === EmailIntegrationType.GMAIL
        ? isSuccessGoogle
        : integrationTypeToTest === EmailIntegrationType.OUTLOOK
          ? isSuccessOutlook
          : integrationTypeToTest === EmailIntegrationType.CUSTOM
            ? isSuccessCustomEmailProvider
            : isSuccessGoogle ||
              isSuccessOutlook ||
              isSuccessCustomEmailProvider,
    error:
      integrationTypeToTest === EmailIntegrationType.GMAIL
        ? errorGoogle
        : integrationTypeToTest === EmailIntegrationType.OUTLOOK
          ? errorOutlook
          : integrationTypeToTest === EmailIntegrationType.CUSTOM
            ? errorCustomEmailProvider
            : errorGoogle || errorOutlook || errorCustomEmailProvider,
    data:
      integrationTypeToTest === EmailIntegrationType.GMAIL
        ? dataGoogle
        : integrationTypeToTest === EmailIntegrationType.OUTLOOK
          ? dataOutlook
          : integrationTypeToTest === EmailIntegrationType.CUSTOM
            ? dataCustomEmailProvider
            : dataGoogle || dataOutlook || dataCustomEmailProvider,
    verifyIntegration: handleVerify,
    // isLoadingGoogle,
    // isSuccessGoogle,
    // isErrorGoogle,
    // verifyGoogleIntegration,
    // errorGoogle,
    // isLoadingOutlook,
    // isSuccessOutlook,
    // isErrorOutlook,
    // verifyOutlookIntegration,
    // errorOutlook,
  };
};

export default useEmailIntegrationGuard;
